import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";

export function formatToMonthDayYear(date: string | Date): string {
  const dateObj = new Date(date);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  return dateObj.toLocaleDateString('en-US', options);
}

export const formatDate = (date: string | Date) => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
}

export const formatDateToISOString = (date: string | Date) => {
    const dateObj = new Date(date);
    return dateObj.toISOString().split('T')[0];
}

export const formatDateToUTC = (date: string | Date) => {
    const dateObj = new Date(date);
    return new Date(dateObj.toUTCString());
}

export const formatDateToLocal = (date: string | Date) => {
    const dateObj = new Date(date);
    return new Date(dateObj.toLocaleString());
}

export const formatDateToLocalString = (date: string | Date) => {
    const dateObj = new Date(date);
    const formattedDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
    return formattedDate;
}


export const getBrowserTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const convertToUTC = (
  date: string | undefined,
  timeZone: string = getBrowserTimeZone()
): string | undefined => {
  if (!date) return undefined;
  return fromZonedTime(date, timeZone).toISOString();
};

export function formatAppointmentEncounterDateTime(date: string | Date, time: string): string {
    const appointmentDate = typeof date === 'string' ? new Date(date) : date;
    return `${format(appointmentDate, 'MMM d, yyyy')} ${formatTime(time)}`;
}

export function formatFullDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, 'EEEE, MMMM d, yyyy');
}

export function stripAMPM(time: string) {
  time = time.trim();
  
  const ampmRegex = /\s*(am|pm)$/i;
  
  return time.replace(ampmRegex, '');
}

export function convertTimeTo24Hour(time: string) {
  time = time.trim();
  
  const ampmRegex = /\s*(am|pm)$/i;
  
  const period = time.match(ampmRegex)?.[0]?.trim().toUpperCase() || "";
  
  const strippedTime = time.replace(ampmRegex, '').trim();
  
  if (!period) {
    return strippedTime;
  }
  
  const [hours, minutes] = strippedTime.split(':');
  let hour = parseInt(hours!, 10);
  
  if (period === "PM" && hour < 12) {
    hour += 12;
  } else if (period === "AM" && hour === 12) {
    hour = 0;
  }
  
  const formattedHour = hour.toString().padStart(2, '0');
  
  return `${formattedHour}:${minutes}`;
}

export function formatTime(time: string): string {
    if (/^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i.test(time)) {
        return time;
    }

    try {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours!, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const formattedHour = hour % 12 || 12; 
        return `${formattedHour}:${minutes} ${ampm}`;
    } catch {
        return time; 
    }
}

export function formatEncounterFullDateTime(datetime: string | Date): string {
    const dateTimeObj = new Date(datetime);
    const date = dateTimeObj.toISOString().split('T')[0] || new Date();
    const time = dateTimeObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
    return `${formatFullDate(date)} ${formatTime(time)}`;
}

export function formatEncounterFullTime(datetime: string | Date): string {
  const dateTimeObj = new Date(datetime);
  const time = dateTimeObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
  });
  return `${formatTime(time)}`;
}

export const combineDateAndTime = (date: Date | string, time: string) => {
    const dateObj = new Date(date);
    const timeObj = new Date(`1970-01-01T${convertTimeTo24Hour(time)}`);
    const combinedDate = new Date(dateObj.getTime() + timeObj.getTime());
    return combinedDate;
}

export function formatEncounterTag(
  date: Date | string,
  time: string
) {
  const dateObj = combineDateAndTime(date, time);
  const now = new Date();

  const diffMs = dateObj.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000); 
  const diffHours = Math.round(diffMs / 3600000); 
  const diffDays = Math.round(diffMs / 86400000); 

  if (Math.abs(diffMins) <= 5) {
    return { status: "happening", tag: 'Happening Now' };
  }

  if (diffMs > 0) {
    if (diffMins < 60) {
      return { status: "upcoming", tag: `in ${diffMins} minute${diffMins !== 1 ? 's' : ''}` };
    }
    if (diffHours < 24) {
      return { status: "upcoming", tag: `in ${diffHours} hour${diffHours !== 1 ? 's' : ''}` };
    }
    if (diffDays < 7) {
      return { status: "upcoming", tag: `in ${diffDays} day${diffDays !== 1 ? 's' : ''}` };
    }
    return { status: "upcoming", tag: 'Upcoming' };
  }

  // Handle past dates
  if (diffMins >= -60) {
    return { status: "past", tag: `${Math.abs(diffMins)} minute${Math.abs(diffMins) !== 1 ? 's' : ''} ago` };
  }

  if (diffHours >= -24) {
    return { status: "past", tag: `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? 's' : ''} ago` };
  }
  
  if (diffDays >= -7) {
    return { status: "past", tag: `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago` };
  }
  
  return { status: "past", tag: "Past" };
}

export function formatEncounterAppointmentStatusTag(
  options: {
    date: Date | string,
    time: string,
    status: "scheduled" | "attended" | "cancelled",
    meetingInterval?: string | number
  }
) {
  const { date, time, status, meetingInterval } = options;

  const dateObj = combineDateAndTime(date, time);
  const now = new Date();

  const intervalMinutes = (typeof meetingInterval === 'string' ? parseInt(meetingInterval, 10) : meetingInterval) || 0;
  
  if (status === "cancelled") {
    return { status, tag: "Reschedule" };
  }

  const diffMs = dateObj.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000); 
  const diffHours = Math.round(diffMs / 3600000); 
  const diffDays = Math.round(diffMs / 86400000);

  
    if (status === "attended" || (status === "scheduled" && diffDays < -7)) {
      return { status: "completed", tag: "Completed" };
    }

    if (status === "scheduled" && diffDays < -7) {
      return { status: "actionNeeded", tag: "Reschedule" };
    }

  if (diffMins >= -intervalMinutes && diffMins <= 5) {
    return { status: "happening", tag: 'Happening Now' };
  }

  if ( status === "scheduled" && diffMs > 0) {
    if (diffMins < 60) {
      return { status: "upcoming", tag: `in ${diffMins} minute${diffMins !== 1 ? 's' : ''}` };
    }
    if (diffHours < 24) {
      return { status: "upcoming", tag: `in ${diffHours} hour${diffHours !== 1 ? 's' : ''}` };
    }
    if (diffDays < 7) {
      return { status: "upcoming", tag: `in ${diffDays} day${diffDays !== 1 ? 's' : ''}` };
    }
    return { status: "upcoming", tag: 'Upcoming' };
  }

  if (diffMins >= -60 && diffMins < -intervalMinutes) {
    return { status: "past", tag: `${Math.abs(diffMins)} minute${Math.abs(diffMins) !== 1 ? 's' : ''} ago` };
  }

  if (diffHours >= -24) {
    return { status: "past", tag: `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? 's' : ''} ago` };
  }
  
  if (diffDays >= -7) {
    return { status: "past", tag: `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago` };
  }
  
  return { status: "past", tag: "Past" };
}

export function formatEncounterStatusTag(
  options: {
    date: Date | string,
    status: "scheduled" | "attended" | "cancelled" | "needs-approval" | "needs-refill" | "needs-review",
    meetingInterval?: string | number
  }
) {
  const { date, status, meetingInterval } = options;

  const dateObj = new Date(date);
  const now = new Date();

  const intervalMinutes = (typeof meetingInterval === 'string' ? parseInt(meetingInterval, 10) : meetingInterval) || 0;

  if (status === "needs-approval") {
    return { status, tag: "Needs Approval" };
  }

  if (status === "needs-refill") {
    return { status, tag: "Needs Refill" };
  }

  if (status === "needs-review") {
    return { status, tag: "Needs Review" };
  }
  
  if (status === "cancelled") {
    return { status, tag: "Reschedule" };
  }

  const diffMs = dateObj.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60000); 
  const diffHours = Math.round(diffMs / 3600000); 
  const diffDays = Math.round(diffMs / 86400000);

  
    if (status === "attended" || (status === "scheduled" && diffDays < -7)) {
      return { status: "completed", tag: "Completed" };
    }

    if (status === "scheduled" && diffDays < -7) {
      return { status: "actionNeeded", tag: "Action Needed" };
    }

  if (diffMins >= -intervalMinutes && diffMins <= 5) {
    return { status: "happening", tag: 'Happening Now' };
  }

  if ( status === "scheduled" && diffMs > 0) {
    if (diffMins < 60) {
      return { status: "upcoming", tag: `in ${diffMins} minute${diffMins !== 1 ? 's' : ''}` };
    }
    if (diffHours < 24) {
      return { status: "upcoming", tag: `in ${diffHours} hour${diffHours !== 1 ? 's' : ''}` };
    }
    if (diffDays < 7) {
      return { status: "upcoming", tag: `in ${diffDays} day${diffDays !== 1 ? 's' : ''}` };
    }
    return { status: "upcoming", tag: 'Upcoming' };
  }

  if (diffMins >= -60 && diffMins < -intervalMinutes) {
    return { status: "past", tag: `${Math.abs(diffMins)} minute${Math.abs(diffMins) !== 1 ? 's' : ''} ago` };
  }

  if (diffHours >= -24) {
    return { status: "past", tag: `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? 's' : ''} ago` };
  }
  
  if (diffDays >= -7) {
    return { status: "past", tag: `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago` };
  }
  
  return { status: "past", tag: "Past" };
}

export function formatEncounterDate(date: Date | string, format: string = 'date'): string {
    const dateObj = date instanceof Date ? date : new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }

    if (format === 'date') {
      return dateObj.toLocaleDateString('en-US', {
        weekday: 'short',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    }
    
    if (format === 'time') {
      return dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    if (format === 'datetime') {
      return `${formatEncounterDate(dateObj, 'date')} ${formatEncounterDate(dateObj, 'time')}`;
    }

    let result = format;
    result = result.replace(/yyyy/g, dateObj.getFullYear().toString());
    result = result.replace(/MM/g, (dateObj.getMonth() + 1).toString().padStart(2, '0'));
    result = result.replace(/dd/g, dateObj.getDate().toString().padStart(2, '0'));
    result = result.replace(/HH/g, dateObj.getHours().toString().padStart(2, '0'));
    // Minutes (mm)
    result = result.replace(/mm/g, dateObj.getMinutes().toString().padStart(2, '0'));
    // Seconds (ss)
    result = result.replace(/ss/g, dateObj.getSeconds().toString().padStart(2, '0'));
    
    return result;
  }


export function formatRefillDate(date: Date | number | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const month = months[dateObj.getMonth()];
  const day = dateObj.getDate();
  const year = dateObj.getFullYear();
  
  return `${month} ${day}, ${year}`;
}

export function formatRefillDateAbbreviated(date: Date | number | string): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  
  const month = months[dateObj.getMonth()];
  const day = dateObj.getDate();
  
  return `${month} ${day}`;
}